/**
 * Prebuilt blocks for the Blocks palette. Each builds top-level editor JSON valid inside a section
 * body (and anywhere else `block+` is allowed — loop, branch, list item, table cell). Blocks are
 * authored as CDM (cdmBuilders.ts) and converted with blocksToPm, so an inserted block is exactly
 * what the same content would be after a save and reload.
 *
 * `upload` blocks have no static content: the palette opens the image upload flow instead.
 * `setup` blocks are data-bound: with the setup dialog mounted, inserting one opens it to pick the
 * list and fields first (blocks/bindingSetup.ts); `build` is the default used without it.
 */
import type { JSONContent } from '@tiptap/core'
import { blocksToPm } from '../cdm/fromCdm'
import { loopScaffoldJson } from '../cdm/fieldInsert'
import { HAlign, type DocBlock } from '../cdm/types'
import { C, DynamicImage, F, Gap, H, P, PA, PageField, Row, T, Table } from './cdmBuilders'
import { currency, generateBinding, type BindingSetup, type BlockSetup, type LineItemsSetup, type TotalsSetup } from './bindingSetup'

export const BLOCK_CATEGORIES = ['Layout', 'Branding', 'Parties', 'Commerce', 'Legal', 'Logic', 'Media'] as const
export type BlockCategory = (typeof BLOCK_CATEGORIES)[number]

export interface CatalogBlock {
  id: string
  name: string
  description: string
  category: BlockCategory
  keywords: string[]
  icon: string
  build(): JSONContent[]
  upload?: boolean
  setup?: BlockSetup
}

const cdm = (...blocks: DocBlock[]) => () => blocksToPm(blocks)
const bound = (setup: BindingSetup) => () => blocksToPm(generateBinding(setup))

const signature = (prefix: string): DocBlock[] => [
  Gap(24),
  P([T('______________________________')]),
  P([F(`${prefix}.name`)]),
  P([F(`${prefix}.title`)]),
  P([T('Date: ______________')]),
]

const usd = currency()

export const DEFAULT_LINE_ITEMS: LineItemsSetup = { kind: 'lineItems', list: 'items', columns: [
  { field: 'description', label: 'Description', formatters: null, align: 'left', width: 228 },
  { field: 'quantity', label: 'Qty', formatters: null, align: 'right', width: 60 },
  { field: 'unitPrice', label: 'Unit price', formatters: usd, align: 'right', width: 90 },
  { field: 'amount', label: 'Amount', formatters: usd, align: 'right', width: 90 },
] }

export const DEFAULT_TOTALS: TotalsSetup = { kind: 'totals', rows: [
  { field: 'subtotal', label: 'Subtotal', formatters: usd, total: false },
  { field: 'tax', label: 'Tax', formatters: usd, total: false },
  { field: 'total', label: 'Total', formatters: usd, total: true },
] }

export const BLOCK_CATALOG: CatalogBlock[] = [
  // Layout
  { id: 'heading', name: 'Heading', description: 'A section heading', category: 'Layout', keywords: ['title', 'h2'], icon: 'H',
    build: cdm(H(2, [T('Heading')])) },
  { id: 'paragraph', name: 'Paragraph', description: 'An empty paragraph of body text', category: 'Layout', keywords: ['text', 'body'], icon: '¶',
    build: cdm(P()) },
  { id: 'two-columns', name: 'Two columns', description: 'Side-by-side content in a borderless table', category: 'Layout', keywords: ['columns', 'grid', 'side by side'], icon: '▥',
    build: cdm(Table([234, 234], false, [Row([C([P([T('Column 1')])]), C([P([T('Column 2')])])])])) },
  { id: 'three-columns', name: 'Three columns', description: 'Three equal columns in a borderless table', category: 'Layout', keywords: ['columns', 'grid'], icon: '▦',
    build: cdm(Table([156, 156, 156], false, [Row([C([P([T('Column 1')])]), C([P([T('Column 2')])]), C([P([T('Column 3')])])])])) },
  { id: 'divider', name: 'Divider', description: 'A horizontal line', category: 'Layout', keywords: ['rule', 'line', 'separator', 'hr'], icon: '—',
    build: cdm({ $type: 'horizontalRule' }) },
  { id: 'spacer', name: 'Spacer', description: 'Vertical white space', category: 'Layout', keywords: ['gap', 'space', 'blank'], icon: '↕',
    build: cdm(Gap(24)) },
  { id: 'page-break', name: 'Page break', description: 'Start a new page', category: 'Layout', keywords: ['new page', 'break'], icon: '⤓',
    build: cdm({ $type: 'pageBreak' }) },

  // Branding
  { id: 'letterhead', name: 'Letterhead', description: 'Company details left, title and number right', category: 'Branding', keywords: ['header', 'company', 'title', 'invoice number'], icon: '🏢',
    build: cdm(Table([300, 168], false, [Row([
      C([P([F('company.name')]), P([F('company.address')]), P([F('company.email')])]),
      C([PA(HAlign.Right, [T('Document Title', { bold: true, fontSizePt: 20 })], 2), PA(HAlign.Right, [T('No. '), F('documentNumber')])]),
    ])])) },
  { id: 'logo', name: 'Logo', description: 'An image filled from {{company.logo}}', category: 'Branding', keywords: ['image', 'brand', 'picture'], icon: '🖼',
    build: cdm(DynamicImage('company.logo', 120, 48)) },
  { id: 'footer-note', name: 'Footer note', description: 'Small centred text with the page number', category: 'Branding', keywords: ['footer', 'page number', 'fine print'], icon: '#',
    build: cdm(P([T('Thank you for your business · Page ', { fontSizePt: 8 }), PageField({ fontSizePt: 8 })], { alignment: HAlign.Center })) },

  // Parties
  { id: 'address', name: 'Address block', description: 'Name and postal address', category: 'Parties', keywords: ['address', 'recipient', 'contact'], icon: '✉',
    build: cdm(P([F('name')]), P([F('address.line1')]), P([F('address.line2')]), P([F('address.city'), T(', '), F('address.state'), T(' '), F('address.postalCode')])) },
  { id: 'bill-ship', name: 'Bill to / Ship to', description: 'Billing and shipping parties side by side', category: 'Parties', keywords: ['billing', 'shipping', 'customer', 'address'], icon: '⇄',
    build: cdm(Table([234, 234], false, [Row([
      C([P([T('Bill to', { bold: true })]), P([F('billTo.name')]), P([F('billTo.address')])]),
      C([P([T('Ship to', { bold: true })]), P([F('shipTo.name')]), P([F('shipTo.address')])]),
    ])])) },

  // Commerce
  { id: 'line-items', name: 'Line items table', description: 'Header row plus one row per item in {{items}}', category: 'Commerce', keywords: ['items', 'products', 'invoice', 'table', 'repeat', 'rows'], icon: '☰',
    setup: 'lineItems', build: bound(DEFAULT_LINE_ITEMS) },
  { id: 'totals', name: 'Totals box', description: 'Subtotal, tax and total, right-aligned', category: 'Commerce', keywords: ['subtotal', 'tax', 'total', 'sum', 'invoice'], icon: 'Σ',
    setup: 'totals', build: bound(DEFAULT_TOTALS) },
  { id: 'payment-terms', name: 'Payment terms', description: 'Heading with {{paymentTerms}}', category: 'Commerce', keywords: ['terms', 'due', 'payment'], icon: '💳',
    build: cdm(H(3, [T('Payment terms')]), P([F('paymentTerms')])) },

  // Legal
  { id: 'clause', name: 'Clause', description: 'Numbered clause heading and justified text', category: 'Legal', keywords: ['section', 'contract', 'term', 'article'], icon: '§',
    build: cdm(H(3, [T('1. Title')]), P([T('Clause text.')], { alignment: HAlign.Justify, spaceAfterPt: 6 })) },
  { id: 'signature', name: 'Signature line', description: 'Signature, name, title and date lines', category: 'Legal', keywords: ['sign', 'signatory', 'execution'], icon: '✍',
    build: cdm(...signature('signatory')) },
  { id: 'two-signatures', name: 'Two-party signatures', description: 'Signature lines for both parties side by side', category: 'Legal', keywords: ['sign', 'parties', 'agreement', 'countersign'], icon: '✍✍',
    build: cdm(Table([234, 234], false, [Row([C(signature('partyA')), C(signature('partyB'))])])) },
  { id: 'date-line', name: 'Date line', description: 'Date: {{date}} written out in full', category: 'Legal', keywords: ['date', 'dated'], icon: '📅',
    build: cdm(P([T('Date: '), F('date', 'datelong')])) },

  // Logic
  { id: 'conditional', name: 'Conditional section', description: 'Shown only when {{showSection}} is true', category: 'Logic', keywords: ['if', 'optional', 'show', 'hide', 'condition'], icon: '⎇',
    setup: 'conditional', build: bound({ kind: 'conditional', field: 'showSection', negate: false, withElse: false }) },
  { id: 'repeating', name: 'Repeating section', description: 'Repeats once per entry in {{items}}', category: 'Logic', keywords: ['loop', 'each', 'repeat', 'list'], icon: '↻',
    setup: 'repeating', build: () => [loopScaffoldJson('items', ['name'])] },

  // Media
  { id: 'image', name: 'Image', description: 'Upload a picture', category: 'Media', keywords: ['picture', 'photo', 'upload'], icon: '🖼',
    build: () => [], upload: true },
  { id: 'qr', name: 'QR code', description: 'QR code from {{qrValue}}', category: 'Media', keywords: ['qr', 'code', 'scan', 'link'], icon: '▣',
    build: cdm(P([F('qrValue', 'qr')])) },
  { id: 'barcode', name: 'Barcode', description: 'Code 128 barcode from {{barcodeValue}}', category: 'Media', keywords: ['barcode', 'code128', 'sku'], icon: '║',
    build: cdm(P([F('barcodeValue', 'code128')])) },
  { id: 'chart', name: 'Chart', description: 'Bar chart from {{chartData}}', category: 'Media', keywords: ['chart', 'graph', 'bar'], icon: '📊',
    build: cdm(P([F('chartData', 'bar')])) },
]

export const catalogBlock = (id: string): CatalogBlock | undefined => BLOCK_CATALOG.find(b => b.id === id)

export function searchCatalog(query: string, blocks: CatalogBlock[] = BLOCK_CATALOG): CatalogBlock[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!terms.length) return blocks
  return blocks.filter(b => {
    const hay = [b.name, b.description, b.category, ...b.keywords].join(' ').toLowerCase()
    return terms.every(t => hay.includes(t))
  })
}
